/**
 * SeamsAndBridgesPanel — the four structural seams and their bridges.
 *
 * Renders each seam (Sᴬ, Sᴿ, Sᵁ, Sᴹ) with its bridge constructor (or
 * the explicit "no bridge" for admission), the schema id of the bridge
 * artifact, and the primary rulebook codes that fire when the seam is
 * crossed without its bridge.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { SeamBridgeMap } from "../components/SeamBridgeMap.js";
import seamsData from "../data/seams.json" with { type: "json" };

const ACCENT = "#ef4444";

interface Bridge {
  readonly id: string;
  readonly label: string;
  readonly schemaId: string;
}

interface Seam {
  readonly id: string;
  readonly symbol: string;
  readonly label: string;
  readonly tagline: string;
  readonly summary: string;
  readonly bridge: Bridge | null;
  readonly bridgeReason: string;
  readonly primaryRulebookCodes: readonly string[];
  readonly accent: string;
}

const SEAMS: readonly Seam[] = seamsData.seams;

export function SeamsAndBridgesPanel(): React.JSX.Element {
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  return (
    <PanelSurface.PanelShell
      title="Seams & Bridges"
      subtitle="Four structural seams · at most one bridge per bridgeable seam"
      accentColor={ACCENT}
    >
      <PanelSurface.Card>
        <p className="m-0 text-[14.5px] leading-[1.75] text-[var(--ilg-text)]">
          A <strong>seam</strong> is a class of morphism in <PanelSurface.InlineCode>𝓥</PanelSurface.InlineCode>{" "}
          that is <strong>not liftable by default</strong> in{" "}
          <PanelSurface.InlineCode>𝓔_I</PanelSurface.InlineCode>. A <strong>bridge</strong> is the designated
          artifact constructor, if one exists, that re-inhabits the lift. The
          bridge multiplicity rule is non-negotiable: introducing a second
          bridge per bridgeable seam weakens the theorem and dissolves the seam.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card glowColor="rgba(239,68,68,0.06)">
        <PanelSurface.SectionHeading>Seam–bridge map</PanelSurface.SectionHeading>
        <p className="m-0 mt-2.5 mb-1 text-[12.5px] text-[var(--ilg-muted)] leading-[1.6]">
          Hover a seam to highlight it; below each seam is the unique bridge
          (or, for Sᴬ, the structural refusal).
        </p>
        <div className="mt-1.5">
          <SeamBridgeMap
            highlightedSeamId={highlightedId}
            onHighlight={setHighlightedId}
          />
        </div>
      </PanelSurface.Card>

      <div
        className="grid gap-3.5"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
        }}
      >
        {SEAMS.map((seam) => (
          <div
            key={seam.id}
            onMouseEnter={() => {
              setHighlightedId(seam.id);
            }}
            onMouseLeave={() => {
              setHighlightedId(null);
            }}
          >
            <PanelSurface.Card
              glowColor={
                highlightedId === seam.id
                  ? `${seam.accent}33`
                  : `${seam.accent}18`
              }
              style={{
                borderColor:
                  highlightedId === seam.id ? seam.accent : `${seam.accent}66`,
                transition: "all 160ms ease",
                transform:
                  highlightedId === seam.id ? "translateY(-2px)" : undefined,
              }}
            >
              <div className="flex items-baseline gap-3">
                <span
                  className="font-mono text-[24px] font-extrabold leading-none"
                  style={{
                    color: seam.accent,
                    textShadow: `0 0 12px ${seam.accent}55`,
                  }}
                >
                  {seam.symbol}
                </span>
                <span className="text-[16px] font-bold text-[var(--ilg-bright)]">
                  {seam.label}
                </span>
              </div>
              <p className="m-0 mt-1.5 text-[12.5px] text-[var(--ilg-muted)] font-mono tracking-[0.04em]">
                {seam.tagline}
              </p>
              <p className="mt-3.5 text-[14px] leading-[1.7] text-[var(--ilg-text)]">
                {seam.summary}
              </p>

              <div className="mt-4">
                <PanelSurface.SectionHeading>Bridge</PanelSurface.SectionHeading>
                {seam.bridge ? (
                  <div
                    className="mt-2 px-3.5 py-2.5 rounded-[10px]"
                    style={{
                      background: `${seam.accent}10`,
                      border: `1px solid ${seam.accent}55`,
                    }}
                  >
                    <div
                      className="font-mono text-[13.5px] font-bold"
                      style={{
                        color: seam.accent,
                      }}
                    >
                      {seam.bridge.label}
                    </div>
                    <div className="mt-1 text-[11px] text-[var(--ilg-muted)] font-mono">
                      {seam.bridge.schemaId}
                    </div>
                  </div>
                ) : (
                  <div
                    className="mt-2 px-3.5 py-2.5 bg-[rgba(239,68,68,0.10)] rounded-[10px] font-mono text-[13px] font-semibold"
                    style={{
                      border: `1px solid ${panelTokens.accentRed}55`,
                      color: panelTokens.accentRed,
                    }}
                  >
                    none — unknown ⇒ refuse
                  </div>
                )}
                <p className="mt-2.5 text-[13px] text-[var(--ilg-text)] leading-[1.6]">
                  {seam.bridgeReason}
                </p>
              </div>

              <div className="mt-4">
                <PanelSurface.SectionHeading>Primary rulebook codes</PanelSurface.SectionHeading>
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  {seam.primaryRulebookCodes.map((code) =>
                    PanelSurface.badge(code, seam.accent),
                  )}
                </div>
              </div>
            </PanelSurface.Card>
          </div>
        ))}
      </div>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Why bridge multiplicity is forbidden</PanelSurface.SectionHeading>
        <p className="mt-3.5 text-[14px] leading-[1.7] text-[var(--ilg-text)]">
          Each bridgeable seam isolates one class of evidence-shape the lift
          must carry; Sᴬ deliberately has no bridge. A second bridge would offer
          an alternative path that discharges the same seam with weaker evidence
          — a structural waiver. The Controlled Bridges condition therefore
          reads:{" "}
          <em>
            per bridgeable seam, the bridge constructor is unique up to the
            schema
          </em>
          . The Phase-8 promotion gate refuses any candidate extension that
          introduces a parallel bridge.
        </p>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
